/**
 * Enhanced Country Detection with API Fallback
 * 
 * Detecta el país del usuario con múltiples fuentes:
 * 1. Cloudflare Worker config (instantáneo)
 * 2. Timezone + idioma del navegador (instantáneo)
 * 3. API de geolocalización (< 500ms)
 * 
 * Optimizado para UX: devuelve resultado rápido y actualiza si la API mejora la precisión.
 */

import { useState, useEffect, useRef } from "react";
import { detectCountryCode } from "./countryDetection";

interface CountryDetectionResult {
  country: string | null;
  isLoading: boolean;
  confidence: "high" | "medium" | "low" | "unknown";
  source: "worker" | "timezone" | "api" | "browser_lang" | "unknown";
  city?: string | null;
  region?: string | null;
}

/**
 * Hook para detectar país con API fallback
 * 
 * @param enableApiDetection - Si true, usa API como fallback (default: true)
 * @param timeoutMs - Timeout para API en ms (default: 800ms)
 * @returns Resultado de detección con país, loading state y confianza
 */
export function useCountryDetectionWithApi(
  enableApiDetection = true,
  timeoutMs = 800
): CountryDetectionResult {
  const [result, setResult] = useState<CountryDetectionResult>(() => {
    // Detección inicial instantánea (sincrónica)
    const localCountry = detectCountryCode();
    
    if (typeof window === "undefined") {
      return { country: null, isLoading: false, confidence: "unknown", source: "unknown" };
    }

    // Prioridad 1: Cloudflare Worker (más preciso)
    const workerConfig = (window as any).__TEMPLATE_CONFIG__;
    if (workerConfig?.country) {
      return {
        country: workerConfig.country,
        isLoading: false,
        confidence: "high",
        source: "worker",
        city: workerConfig.city || null,
        region: workerConfig.region || null,
      };
    }

    // Prioridad 2: Timezone (buena precisión)
    if (localCountry) {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const hasTimezoneMatch = timezone.includes("Argentina") || 
                               timezone.includes("Buenos_Aires") ||
                               timezone.includes("Cordoba");
      
      return {
        country: localCountry,
        isLoading: enableApiDetection, // Si API está habilitada, seguimos loading
        confidence: hasTimezoneMatch ? "high" : "medium",
        source: "timezone",
      };
    }

    // Sin detección local - necesitamos API
    return {
      country: null,
      isLoading: enableApiDetection,
      confidence: "unknown",
      source: "unknown",
    };
  });

  const apiCalled = useRef(false);

  useEffect(() => {
    // No llamar API si:
    // 1. Ya tenemos alta confianza (Cloudflare Worker)
    // 2. API está deshabilitada
    // 3. Ya se llamó antes
    if (result.confidence === "high" || !enableApiDetection || apiCalled.current) {
      return;
    }

    apiCalled.current = true;

    // Llamar API con timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const detectViaApi = async () => {
      try {
        // Estrategia dual: intentar Cloudflare primero, luego nuestro backend
        let apiCountry: string | null = null;

        // Método 1: Cloudflare trace (más rápido, pero puede fallar en algunos navegadores)
        try {
          const cfResponse = await fetch("https://www.cloudflare.com/cdn-cgi/trace", {
            signal: controller.signal,
          });

          if (cfResponse.ok) {
            const text = await cfResponse.text();
            const lines = text.split("\n");
            const locLine = lines.find(line => line.startsWith("loc="));
            
            if (locLine) {
              apiCountry = locLine.split("=")[1]?.trim().toUpperCase();
            }
          }
        } catch (cfError) {
          console.log("[useCountryDetectionWithApi] Cloudflare trace failed, trying backend");
        }

        // Método 2: Nuestro backend (siempre funciona, usa headers de Vercel/Cloudflare)
        let apiCity: string | null = null;
        let apiRegion: string | null = null;
        
        if (!apiCountry) {
          const backendResponse = await fetch("/api/detect-country", {
            signal: controller.signal,
          });

          if (backendResponse.ok) {
            const data = await backendResponse.json() as { 
              country?: string; 
              city?: string;
              region?: string;
            };
            apiCountry = data.country?.toUpperCase() || null;
            apiCity = data.city || null;
            apiRegion = data.region || null;
          }
        }

        // Si obtuvimos país válido de la API
        if (apiCountry && apiCountry.length === 2 && apiCountry !== "XX") {
          console.log("[useCountryDetectionWithApi] API detected country:", {
            country: apiCountry,
            city: apiCity,
            region: apiRegion,
          });
          setResult({
            country: apiCountry,
            isLoading: false,
            confidence: "high",
            source: "api",
            city: apiCity,
            region: apiRegion,
          });
          return;
        }

        // API no devolvió país válido - mantener detección local
        throw new Error("Invalid API response");
      } catch (error) {
        // Timeout o error - usar detección local
        const isAbortError = error instanceof Error && error.name === "AbortError";
        console.log(
          `[useCountryDetectionWithApi] API ${isAbortError ? "timeout" : "error"} - using local detection:`,
          error
        );
        
        const localCountry = result.country || detectCountryCode();
        setResult({
          country: localCountry,
          isLoading: false,
          confidence: localCountry ? "medium" : "low",
          source: localCountry ? "timezone" : "unknown",
        });
      } finally {
        clearTimeout(timeoutId);
      }
    };

    detectViaApi();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [enableApiDetection, timeoutMs, result.confidence, result.country]);

  return result;
}

/**
 * Detecta si el país es Argentina con alta confianza
 * Útil para decisiones críticas de routing
 */
export function useIsArgentina(enableApiDetection = true): {
  isArgentina: boolean;
  isLoading: boolean;
  confidence: "high" | "medium" | "low" | "unknown";
} {
  const detection = useCountryDetectionWithApi(enableApiDetection);
  
  return {
    isArgentina: detection.country === "AR",
    isLoading: detection.isLoading,
    confidence: detection.confidence,
  };
}
